import {
  createContext,
  Dispatch,
  ReactNode,
  useContext,
  useReducer
} from 'react'

import { ImagesTab } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseImageGallery/HouseImageGallery'

export interface FocusViewImage {
  src: string
  alt: string
}

type Action =
  | {
      type: 'openFocusView'
      imageSources: FocusViewImage[]
      displayImgIndex: number
    }
  | { type: 'closeFocusView' }
  | { type: 'changeFocusViewImage'; displayImgIndex: number }
  | { type: 'openGallery'; initialTabIndex?: number }
  | {
      type: 'openFloorPlan'
      imageSources: FocusViewImage[]
      displayImgIndex: number
    }
  | { type: 'closeGallery' }

type State = {
  galleryOpen: boolean
  focusView: {
    open: boolean
    imageSources?: FocusViewImage[]
    displayImgIndex: number
  }
  initialTabIndex?: number
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'openFocusView':
      return {
        ...state,
        focusView: {
          open: true,
          imageSources: action.imageSources,
          displayImgIndex: action.displayImgIndex
        }
      }
    case 'closeFocusView':
      return { ...state, focusView: { open: false, displayImgIndex: 0 } }
    case 'changeFocusViewImage':
      return {
        ...state,
        focusView: {
          ...state.focusView,
          displayImgIndex: action.displayImgIndex
        }
      }
    case 'openGallery':
      return {
        ...state,
        galleryOpen: true,
        initialTabIndex: action.initialTabIndex
      }
    case 'openFloorPlan':
      return {
        ...state,
        galleryOpen: true,
        focusView: {
          open: true,
          imageSources: action.imageSources,
          displayImgIndex: action.displayImgIndex
        },
        initialTabIndex: ImagesTab.floorPlans
      }
    case 'closeGallery':
      return { ...state, galleryOpen: false }
    default:
      return state
  }
}

const ImageGalleryContext = createContext<
  | {
      state: State
      dispatch: Dispatch<Action>
    }
  | undefined
>(undefined)

export function useImageGalleryContext() {
  const context = useContext(ImageGalleryContext)

  if (!context) {
    throw new Error(
      'useImageGalleryContext must be used within ImageGalleryContextProvider'
    )
  }

  return context
}

export function ImageGalleryContextProvider(props: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, {
    galleryOpen: false,
    focusView: { open: false, displayImgIndex: 0 }
  })

  return (
    <ImageGalleryContext.Provider value={{ state, dispatch }}>
      {props.children}
    </ImageGalleryContext.Provider>
  )
}
